<template lang="pug">
  #AdminSuperMemberBadges
    BNotification(:active.sync="showError" type="is-danger" :duration="3000")
      | {{ errorMessage }}
    BNotification(:active.sync="showSuccess" type="is-success" :duration="3000")
      | {{ successMessage }}

    .box.bordered
      WtTitle(h3) Manage Member Badges

      .mb-4
        BField(label="Select Badge Category")
          BSelect(
            v-model="selectedCategory"
            placeholder="Choose a category"
            :loading="categoriesLoading"
          )
            option(value="") -- Select Category --
            option(value="activity") Activity
            option(value="social") Social
            option(value="wellness") Wellness

      .mb-4(v-if="selectedCategory && !categoriesLoading")
        BField(label="Select Badge")
          BSelect(
            v-model="selectedBadgeId"
            placeholder="Choose a badge"
            :loading="badgesLoading"
            :disabled="availableUnassignedBadges.length === 0"
          )
            option(value="") -- Select Badge --
            option(
              v-for="badge in availableUnassignedBadges"
              :key="badge.id"
              :value="badge.id"
            ) {{ badge.name }}
          p.help.has-text-warning(v-if="availableUnassignedBadges.length === 0")
            | All badges in this category have been assigned to this member.

      .mb-4
        BField(label="Badge Date (optional)")
          BDatepicker(
            v-model="selectedBadgeDate"
            placeholder="Click to select..."
            :max-date="today"
            :min-date="member ? new Date(member.registeredDate) : null"
          )

      WtButton(
        @click.native="addBadge"
        :loading="addingBadge"
        :disabled="!selectedBadgeId"
      ) Add Badge

      .mt-6(v-if="memberBadges && memberBadges.length > 0")
        WtTitle(h4) Current Badges
        .table-container
          table.table.is-fullwidth.is-hoverable
            thead
              tr
                th Badge Name
                th Badge Issue Date
                th Actions
            tbody
              tr(v-for="badge in memberBadges" :key="badge.id")
                td {{ badge.badge.name }}
                td
                  .is-flex.is-justify-content-space-between.is-align-items-center
                    span {{ moment(badge.issueDate).format("YYYY-MM-DD") }}
                    WtButton(
                      size="is-small"
                      @click.native="openDateModal(badge)"
                    ) Edit Date
                td
                  WtButton(
                    is-text
                    @click.native="deleteBadge(badge.id)"
                    :loading="deletingBadge === badge.id"
                  )
                    BIcon(icon="delete" type="is-danger")

      .mt-4(v-else)
        p.has-text-grey No badges assigned to this member.

    BModal(v-model="showDateModal" has-modal-card trap-focus)
      template(slot="default" slot-scope="props")
        .modal-card
          .modal-card-head
            p.modal-card-title Edit Badge Issue Date
            button.delete(type="button" @click="props.close")
          .modal-card-body
            BField(label="Badge Issue Date")
              BDatepicker(
                v-model="editingBadge.badgeForDate"
                placeholder="Click to select..."
                :min-date="member ? new Date(member.registeredDate) : null"
                :max-date="today"
              )
          .modal-card-foot
            .level
              .level-left
              .level-right
                .level-item
                  WtButton(
                    @click.native="closeAndSaveDate"
                    :loading="updatingBadge === editingBadge.id"
                  ) Save Date
                .level-item
                  WtButton(
                    @click.native="showDateModal = false"
                    is-outline
                    type="is-grey"
                  ) Cancel
</template>

<script>
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import moment from "moment";

export default {
  name: "AdminSuperMemberBadges",

  components: {
    WtButton,
    WtTitle
  },

  props: {
    memberId: {
      type: Number,
      required: true
    },
    member: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      selectedCategory: "",
      selectedBadgeId: "",
      selectedBadgeDate: null,
      memberBadges: null,
      availableBadges: [],
      categoriesLoading: false,
      badgesLoading: false,
      addingBadge: false,
      updatingBadge: null,
      deletingBadge: null,
      showError: false,
      showSuccess: false,
      errorMessage: "",
      successMessage: "",
      showDateModal: false,
      editingBadge: { badgeForDate: null, id: null },
      today: new Date()
    };
  },

  computed: {
    availableUnassignedBadges() {
      if (!this.memberBadges) return this.availableBadges;
      const assignedBadgeIds = new Set(this.memberBadges.map(b => b.badgeId));
      return this.availableBadges.filter(b => !assignedBadgeIds.has(b.id));
    }
  },

  watch: {
    selectedCategory(newVal) {
      if (newVal) {
        this.fetchBadges(newVal);
      } else {
        this.availableBadges = [];
        this.selectedBadgeId = "";
      }
    }
  },

  mounted() {
    this.fetchMemberBadges();
  },

  methods: {
    moment,

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm");
    },

    async fetchBadges(category) {
      try {
        this.badgesLoading = true;
        const response = await this.axios.get(`badges/${category}`);
        this.availableBadges = response.data.data || [];
      } catch (error) {
        console.error("Error fetching badges:", error);
        this.showErrorMessage("Failed to fetch badges");
      } finally {
        this.badgesLoading = false;
      }
    },

    async fetchMemberBadges() {
      try {
        const response = await this.axios.get(
          `members/${this.memberId}/badges`
        );
        this.memberBadges = response.data.data || [];
      } catch (error) {
        console.error("Error fetching member badges:", error);
        this.showErrorMessage("Failed to fetch member badges");
      }
    },

    async addBadge() {
      if (!this.selectedBadgeId) {
        this.showErrorMessage("Please select a badge");
        return;
      }

      if (
        this.selectedBadgeDate &&
        moment(this.selectedBadgeDate).isAfter(moment(), "day")
      ) {
        this.showErrorMessage("Badge date cannot be in the future");
        return;
      }

      if (
        this.selectedBadgeDate &&
        this.member &&
        moment(this.selectedBadgeDate).isBefore(
          this.member.registeredDate,
          "day"
        )
      ) {
        const registeredDateFormatted = moment(
          this.member.registeredDate
        ).format("YYYY-MM-DD");
        this.showErrorMessage(
          `Badge date cannot be before member's registration date (${registeredDateFormatted})`
        );
        return;
      }

      try {
        this.addingBadge = true;
        const payload = {
          badges: [
            {
              badgeId: this.selectedBadgeId,
              badgeForDate: this.selectedBadgeDate
                ? moment(this.selectedBadgeDate).format("YYYY-MM-DD")
                : null
            }
          ]
        };

        await this.axios.post(`members/${this.memberId}/badges`, payload);
        this.showSuccessMessage("Badge added successfully");
        this.selectedBadgeId = "";
        this.selectedBadgeDate = null;
        this.selectedCategory = "";
        this.fetchMemberBadges();
      } catch (error) {
        console.error("Error adding badge:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to add badge";
        this.showErrorMessage(errorMessage);
      } finally {
        this.addingBadge = false;
      }
    },

    openDateModal(badge) {
      this.editingBadge = {
        id: badge.id,
        badgeForDate: badge.issueDate ? new Date(badge.issueDate) : null
      };
      this.showDateModal = true;
    },

    async closeAndSaveDate() {
      if (!this.editingBadge.badgeForDate) {
        this.showErrorMessage("Please select a date");
        return;
      }

      if (moment(this.editingBadge.badgeForDate).isAfter(moment(), "day")) {
        this.showErrorMessage("Badge date cannot be in the future");
        return;
      }

      if (
        this.member &&
        moment(this.editingBadge.badgeForDate).isBefore(
          this.member.registeredDate,
          "day"
        )
      ) {
        const registeredDateFormatted = moment(
          this.member.registeredDate
        ).format("YYYY-MM-DD");
        this.showErrorMessage(
          `Badge date cannot be before member's registration date (${registeredDateFormatted})`
        );
        return;
      }

      try {
        this.updatingBadge = this.editingBadge.id;
        const payload = {
          badgeForDate: moment(this.editingBadge.badgeForDate).format(
            "YYYY-MM-DD"
          )
        };

        await this.axios.put(
          `members/${this.memberId}/badges/${this.editingBadge.id}`,
          payload
        );
        this.showSuccessMessage("Badge date updated successfully");
        this.showDateModal = false;
        this.fetchMemberBadges();
      } catch (error) {
        console.error("Error updating badge date:", error);
        this.showErrorMessage("Failed to update badge date");
      } finally {
        this.updatingBadge = null;
      }
    },

    async updateBadgeDate(badge) {
      if (!badge.badgeForDate) {
        this.showErrorMessage("Please select a date");
        return;
      }

      try {
        this.updatingBadge = badge.id;
        const payload = {
          badgeForDate: moment(badge.badgeForDate).format("YYYY-MM-DD")
        };

        await this.axios.put(
          `members/${this.memberId}/badges/${badge.id}`,
          payload
        );
        this.showSuccessMessage("Badge date updated successfully");
        this.fetchMemberBadges();
      } catch (error) {
        console.error("Error updating badge date:", error);
        this.showErrorMessage("Failed to update badge date");
      } finally {
        this.updatingBadge = null;
      }
    },

    async deleteBadge(badgeId) {
      if (confirm("Are you sure you want to delete this badge?")) {
        try {
          this.deletingBadge = badgeId;
          await this.axios.delete(`members/${this.memberId}/badges/${badgeId}`);
          this.showSuccessMessage("Badge deleted successfully");
          this.fetchMemberBadges();
        } catch (error) {
          console.error("Error deleting badge:", error);
          this.showErrorMessage("Failed to delete badge");
        } finally {
          this.deletingBadge = null;
        }
      }
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.showError = true;
    },

    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccess = true;
    }
  }
};
</script>

<style lang="sass" scoped>
#AdminSuperMemberBadges
  .table-container
    overflow-x: auto
    overflow-y: visible

  table
    margin-top: 1rem

  td
    overflow: visible !important

  /deep/ .modal-card
    min-height: 70vh

  /deep/ .modal-card-body
    min-height: 60vh
</style>
